/**
 * Overheat — the belt-fed conversion's cost.
 *
 * A belt feed removes the reload, which is the single largest brake on an LMG's
 * time-on-target. Something has to replace it or the attachment is a pure upgrade.
 * Heat is that something, and it is a strictly worse brake than a reload in the way
 * that matters: a reload is *scheduled* by the player, and a lockout is not.
 *
 * The curve, in order:
 *
 *  - Every round adds `heatPerShot`. At the default 0.055 the bar fills in 18
 *    rounds of continuous fire, roughly two seconds at LMG cadence.
 *  - Cooling does not start for `coolDelay` after the last round, so tapping the
 *    trigger is not a free bypass.
 *  - At 1.0 the weapon locks. It stays locked until heat falls back through
 *    `resumeAt` — not to zero, so a disciplined gunner gets back in the fight
 *    quickly, and not at 1.0 either, or the lockout would flicker.
 *  - While locked, heat sheds at `ventRate`, which is deliberately *slower* than
 *    the normal `coolRate`. The lockout is the punishment; a vent that clears
 *    faster than idle cooling would make overheating the optimal way to cool.
 *
 * ## How the lockout is enforced
 *
 * `WeaponSystem.fireInhibit` — a boolean that module exposes for exactly this, and
 * checks in `_handleFire` after the trigger latch and *before* the ammunition test,
 * so a locked-out weapon neither burns a round nor dry-clicks. It is written every
 * frame the lockout is live and cleared the moment it is not, which means nothing
 * here can leave the weapon inhibited if this module stops running.
 *
 * An earlier version held `nextShotAt` ahead of the clock instead. That worked, but
 * it was this module reaching into the weapon's own cadence bookkeeping and it had
 * to special-case burst continuation, which runs off `burstNextAt` and bypasses
 * `nextShotAt` entirely. The dedicated gate has neither problem.
 */

export const TUNING = {
  /**
   * Publish the readout when the bar moves this far, or on any state change.
   * 0.02 is ~15 events a second while cooling, which is under the minimap's own
   * redraw rate and finer than a HUD bar can show.
   */
  publishStep: 0.02,
};

export class Overheat {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;

    this.heat = 0;
    this.locked = false;
    this.shots = 0;
    this.lockouts = 0;
    this._lastShotAt = -99;
    this._published = -1;
    this._publishedLocked = null;

    const ev = ctx.events;
    this._offs = [
      ev.on('weapon:fire', () => this._onShot()),
      // Heat belongs to the gun in your hands. Swapping to the pistol and back
      // must not carry it across, and must not silently keep the old gun locked.
      ev.on('weapon:switch', () => this._clear()),
      ev.on('game:state', (e) => {
        if (e?.state !== 'playing') this._clear();
      }),
    ];
  }

  get config() {
    return this.system.config.overheat;
  }

  /**
   * Bar-per-second while locked out.
   *
   * Attachments author `lockTime` — "this locks you out for 2.8 seconds" — because
   * that is the number a player experiences and the number the part's description
   * promises. Turning it into a rate needs `resumeAt`, which belongs to this module,
   * so the conversion is here rather than in the translation layer. That layer used
   * to drop `lockTime` on the floor, which left every lockout on the roster the same
   * 2.75s no matter what its part claimed.
   */
  get ventRate() {
    const cfg = this.config;
    const lock = cfg.lockTime ?? 0;
    if (!(lock > 0)) return cfg.ventRate;
    return Math.max(0.01, (1 - cfg.resumeAt) / lock);
  }

  onConfigChanged() {
    if (!this.config.enabled) this._clear();
  }

  _clear() {
    if (this.heat === 0 && !this.locked) return;
    this.heat = 0;
    this.locked = false;
    this._publish(true);
  }

  _onShot() {
    const cfg = this.config;
    if (!cfg.enabled) return;
    this.shots++;
    this._lastShotAt = this.ctx.engine?.elapsed ?? 0;
    this.heat = Math.min(1, this.heat + Math.max(0, cfg.heatPerShot));
    if (this.heat >= 1 && !this.locked) {
      this.locked = true;
      this.lockouts++;
      // The one cue the player gets at the instant the gun stops. `dryfire` is the
      // registered "trigger pulled, nothing happened" voice; the audio module
      // ignores ids it does not know, so this is safe either way.
      this.ctx.events.emit('audio:play', { id: 'dryfire', volume: 0.55, pitch: 0.8 });
    }
  }

  update(dt, elapsed) {
    // The gate is re-decided from scratch every frame by whoever wants it held —
    // this module for a lockout, the launcher for its deploy window. Clearing it
    // first means no path can leave the weapon inhibited by accident.
    const ws = this.ctx.weapons;
    if (ws) ws.fireInhibit = false;

    const cfg = this.config;
    if (!cfg.enabled) {
      if (this.heat !== 0 || this.locked) this._clear();
      return;
    }

    /* ---- cool ---- */
    if (this.locked) {
      this.heat = Math.max(0, this.heat - this.ventRate * dt);
      if (this.heat <= cfg.resumeAt) this.locked = false;
    } else if (elapsed - this._lastShotAt >= cfg.coolDelay) {
      this.heat = Math.max(0, this.heat - cfg.coolRate * dt);
    }

    /* ---- enforce ---- */
    if (this.locked) this.hold();

    this._publish(false);
  }

  /**
   * Hold the weapon's own fire gate closed for this frame. Also used by the
   * launcher for its deploy/stow window, which is why it is a method rather than
   * one line inline: both callers run inside the same `'sim'` pass, after the
   * clear above, so whichever of them wants the gate held gets it.
   */
  hold() {
    const ws = this.ctx.weapons;
    if (!ws) return false;
    ws.fireInhibit = true;
    return true;
  }

  _publish(force) {
    const moved = Math.abs(this.heat - this._published) >= TUNING.publishStep;
    if (!force && !moved && this.locked === this._publishedLocked) return;
    this._published = this.heat;
    this._publishedLocked = this.locked;
    this.ctx.events.emit('weapon:heat', {
      /** 0..1. `heat` and `ratio` are the same number; both names are published
       *  because a bar wants a ratio and a readout wants a value, and guessing
       *  wrong costs the HUD agent a round trip. */
      heat: this.heat,
      ratio: this.heat,
      max: 1,
      locked: this.locked,
      /** True while heat is being shed under lockout rather than idly. */
      venting: this.locked,
      resumeAt: this.config.resumeAt,
    });
  }

  report() {
    return {
      enabled: !!this.config.enabled,
      heat: +this.heat.toFixed(3),
      locked: this.locked,
      shots: this.shots,
      lockouts: this.lockouts,
      /** Proof the gate is actually being held, read back off the weapon itself. */
      fireInhibit: this.ctx.weapons ? !!this.ctx.weapons.fireInhibit : null,
      now: +(this.ctx.engine?.elapsed ?? 0).toFixed(3),
    };
  }

  dispose() {
    for (const off of this._offs) off();
    this._offs.length = 0;
  }
}
